<template>
  <div>
    <div v-if="conversationMedia === 'voice'" class="overflow-hidden mt-6">
      <BotConversationRecordingPlayer :conversation="conversation"></BotConversationRecordingPlayer>
    </div>

    <div class="overflow-hidden mt-6">
      <div class="flex items-center border-b border-gray-400 py-2">
        <mdicon name="text" size="18" class="rounded-md bg-gray-300/50 p-1 mr-2 text-gray-500" aria-hidden="true"></mdicon>
        <h2 class="text-base font-semibold leading-7 text-gray-900">Transcript</h2>
      </div>
    </div>

    <div class="flow-root my-4 font-source pt-2 pb-6">
      <ul role="list" class="-mb-8">
        <li v-for="(transcriptItem, transcriptItemIdx) in conversationTranscriptMessages" :key="transcriptItem.ts">
          <div v-if="mustShowMessage(transcriptItem)" class="relative pb-4">
            <span v-if="transcriptItemIdx !== conversationTranscriptMessages.length - 1" class="absolute left-5 top-5 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
            <div class="relative flex items-start space-x-3">
              <div v-if="actorIsCSat(transcriptItem)" :class="['flex h-10 w-10 items-center justify-center', getTranscriptIconClass(transcriptItem)]">{{ transcriptItem.CSat }}</div>
              <mdicon v-else :name="getIcon(transcriptItem)" size="26" :class="['flex h-10 w-10 items-center justify-center', getTranscriptIconClass(transcriptItem)]" />
              <div class="min-w-0 flex-1">
                <div :class="['w-full flex items-center justify-start space-x-2', getTranscriptTitleClass(transcriptItem)]">
                  <div>{{ getAt(transcriptItem) }}</div>
                  <div class="font-medium">{{ getRole(transcriptItem) }}</div>
                  <div class="text-gray-400" v-if="transcriptItem?.datetime">{{ dayjs(transcriptItem.datetime).format() }}</div>
                  <Tag v-if="transcriptItem?.intentId" severity="secondary" :value="transcriptItem.intentId"></Tag>
                  <Tag v-if="transcriptItem?.canceled" severity="warning" value="INTERRUPTED"></Tag>
                </div>
                <!-- TODO Revisit this, does not look good
                <div v-if="actorIsBot(transcriptItem) && transcriptItem?.toolCalls">
                  <div
                    :class="['mt-2 hover:bg-slate-100 cursor-pointer px-2 py-1 -ml-2 rounded-md', transcriptItem.notruncate ? '' : 'max-w-[60vw] truncate', getTranscriptTextClass(transcriptItem)]"
                    @click="transcriptItem.notruncate = !(transcriptItem.notruncate || false)"
                    v-html="getToolCallsMessageHtml(transcriptItem)"
                  ></div>
                </div>
                -->
                <div v-if="actorIsBot(transcriptItem)">
                  <div :class="['mt-2', getTranscriptTextClass(transcriptItem)]" v-html="getBotMessageHtml(transcriptItem)"></div>
                </div>
                <div v-else-if="actorIsSystem(transcriptItem) && transcriptItem?.kind === 'tool_result'">
                  <div
                    :class="['mt-2 hover:bg-slate-100 cursor-pointer px-2 py-1 -ml-2 rounded-md', transcriptItem.notruncate ? 'w-1/2' : 'max-w-[60vw] truncate', getTranscriptTextClass(transcriptItem)]"
                    @click="transcriptItem.notruncate = !(transcriptItem.notruncate || false)"
                    v-html="getToolCallResultMessageHtml(transcriptItem)"
                  ></div>
                </div>
                <div v-else-if="actorIsSystem(transcriptItem)">
                  <div
                    v-if="transcriptItem[textProp].length > 150"
                    :class="['mt-2 hover:bg-slate-100 cursor-pointer px-2 py-1 -ml-2 rounded-md', transcriptItem.notruncate ? '' : 'max-w-[60vw] truncate', getTranscriptTextClass(transcriptItem)]"
                    @click="transcriptItem.notruncate = !(transcriptItem.notruncate || false)"
                  >
                    {{ transcriptItem[textProp] }}
                  </div>
                  <div v-else :class="['mt-2', getTranscriptTextClass(transcriptItem)]">
                    {{ transcriptItem[textProp] }}<span v-if="transcriptItem?.handoffReason" class="font-medium"> : {{ transcriptItem?.handoffReason }}</span>
                  </div>
                  <div v-if="transcriptItem?.kind === 'upload'">
                    <div class="flex items-center justify-start space-x-2 my-4">
                      <div v-for="(fileState, idx) in transcriptItem?.filesStates || []" :key="idx">
                        <Skeleton v-if="fileState.loading" width="100px" height="100px" class="mr-2"></Skeleton>
                        <Image v-else-if="fileState.dataUrl" :src="fileState.dataUrl" alt="Image" width="100" preview />
                        <div v-else>Failed to load image</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="actorIsCSat(transcriptItem) && transcriptItem[textProp].length > 150"
                  :class="['mt-2 hover:bg-slate-100 cursor-pointer px-2 py-1 -ml-2 rounded-md', transcriptItem.notruncate ? '' : 'max-w-[60vw] truncate', getTranscriptTextClass(transcriptItem)]"
                  @click="transcriptItem.notruncate = !(transcriptItem.notruncate || false)"
                  v-html="getCSatMessageHtml(transcriptItem)"
                ></div>
                <div v-else :class="['mt-2', getTranscriptTextClass(transcriptItem)]">
                  <div :class="['mt-2', getTranscriptTextClass(transcriptItem)]" v-html="getBotMessageHtml(transcriptItem)"></div>
                </div>
                <div v-if="messageHasFeedback(transcriptItem)">
                  <div v-for="(feedbackItem, feedbackItemIdx) in feedbackForMessage(transcriptItem)" :key="feedbackItem.ts" class="relative flex items-start space-x-3 mt-3 ml-3 border-b">
                    <!--<div :class="['flex h-10 w-10 items-center justify-center', getTranscriptIconClass(feedbackItem)]">{{ feedbackItem.feedbackScore }}</div>-->
                    <mdicon :name="getIcon(feedbackItem)" size="18" :class="['flex h-8 w-8 items-center justify-center', getTranscriptIconClass(feedbackItem)]" />
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start">
                        <div :class="[getTranscriptTitleClass(feedbackItem), 'mr-2']">{{ getAt(feedbackItem) }}</div>
                        <Rating :model-value="feedbackItem?.feedbackScore" readonly :cancel="false" />
                        <div v-if="feedbackItem?.feedbackFrom" class="text-sm text-gray-500 ml-2">
                          {{ feedbackItem?.feedbackFrom }}
                        </div>
                        <div v-if="feedbackItem?.feedbackFromEmail" class="text-sm font-semibold text-gray-500 ml-2">
                          {{ feedbackItem?.feedbackFromEmail }}
                        </div>
                      </div>
                      <div :class="['mt-2', getTranscriptTextClass(feedbackItem)]" v-html="getFeedbackHtml(feedbackItem)"></div>
                    </div>
                  </div>
                </div>
                <BotMessageSources v-if="messageHasSources(transcriptItem)" :message="transcriptItem" :kbId="kbId"></BotMessageSources>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
  import { computed, reactive, onMounted, watch } from 'vue';
  import { pathOr } from 'ramda';
  import { useMd } from '@/hooks/useMd';
  import { useApi } from '@/hooks/useApi';
  import { formatSeconds } from '@/utils/formatSeconds';
  import Panel from 'primevue/panel';
  import Rating from 'primevue/rating';
  import Tag from 'primevue/tag';
  import Image from 'primevue/image';
  import Skeleton from 'primevue/skeleton';
  import BotConversationRecordingPlayer from '@/components/Bots/analytics/BotConversationRecordingPlayer.vue';
  import BotMessageSources from '@/components/Bots/analytics/BotMessageSources.vue';
  import dayjs from 'dayjs';

  const props = defineProps({
    conversation: {
      type: Object,
      default() {
        return {};
      },
    },
    transcriptMessagesPath: {
      type: Array,
      default() {
        return ['context', 'transcript', 'messages'];
      },
    },
    actorProp: {
      type: String,
      default: 'actor',
    },
    textProp: {
      type: String,
      default: 'message',
    },
    kbId: {
      type: String,
      default: null,
    },
  });

  const md = useMd();
  const api = useApi();

  watch(
    () => props.conversation,
    () => {
      startLoadingImages();
    },
  );

  const conversationId = computed(() => props.conversation?.id || '');
  const conversationMedia = computed(() => props.conversation?.channelAttributes?.media || 'chat');

  const conversationDatetime = computed(() => props.conversation.datetime);
  //const conversationTranscriptMetadata = computed(() => conversationTranscript.value?.metadata || {});

  const conversationFeedbackMessages = computed(() => {
    const feedbackMessages = {};
    const originalMessages = pathOr([], props.transcriptMessagesPath, props.conversation);
    for (let i = 0; i < originalMessages.length; i++) {
      const msg = originalMessages[i];
      if (msg?.role === 'feedback') {
        const answerId = msg?.feedbackAnswerId || null;
        if (answerId) {
          if (Array.isArray(feedbackMessages[answerId])) {
            feedbackMessages[answerId].push(msg);
          } else {
            feedbackMessages[answerId] = [msg];
          }
        }
      }
    }
    return feedbackMessages;
  });

  // Pre-process conversation transcript messages
  // - consolidate feedback under corresponding answer message
  const conversationTranscriptMessages = computed(() => {
    return pathOr([], props.transcriptMessagesPath, props.conversation).filter((m) => m?.role !== 'feedback');
  });

  function getAt(msg) {
    return formatSeconds(Math.floor(((msg?.datetime || 0) - conversationDatetime.value) / 1000));
  }

  // === 'User' ? 'font-medium' : ''

  const actorsInfo = {
    user: {
      name: 'User',
      type: 'user',
      icon: 'account',
      textClass: 'prose font-source text-md text-gray-700 font-medium bg-slate-50 rounded-md px-2 py-1 -ml-2',
      titleClass: 'text-sm',
      iconClass: 'rounded-full bg-slate-100',
    },
    bot: {
      name: 'Bot',
      type: 'bot',
      icon: 'account-multiple',
      textClass: 'prose font-source text-md bg-violet-50 rounded-md px-2 py-1 -ml-2 text-slate-600',
      titleClass: 'text-sm',
      iconClass: 'rounded-full bg-violet-100',
    },
    assistant: {
      name: 'Bot',
      type: 'bot',
      icon: 'account-multiple',
      textClass: 'prose font-source text-md bg-violet-50 rounded-md px-2 py-1 -ml-2 text-slate-600',
      titleClass: 'text-sm',
      iconClass: 'rounded-full bg-violet-100',
    },
    agent: { name: 'Agent', type: 'agent', icon: 'face-agent', textClass: 'text-md text-gray-700 font-medium bg-orange-50 rounded-md px-2 py-1 -ml-2', titleClass: 'text-sm', iconClass: 'rounded-full bg-orange-100' },
    system: {
      name: 'System',
      type: 'system',
      icon: 'cog',
      textClass: 'text-sm text-gray-500',
      titleClass: 'text-xs text-gray-500',
      iconClass: 'rounded-full bg-white text-slate-300 ring-8 ring-white',
    },
    csat: {
      name: 'CSat',
      type: 'csat',
      icon: 'cog',
      textClass: 'prose font-source text-xs text-slate-600',
      titleClass: 'text-xs text-gray-600',
      iconClass: 'rounded-full text-slate-700 font-medium',
    },
    tool: {
      name: 'System',
      type: 'system',
      icon: 'cog',
      textClass: 'text-sm text-gray-500',
      titleClass: 'text-xs text-gray-500',
      iconClass: 'rounded-full bg-white text-slate-300 ring-8 ring-white',
    },
    feedback: {
      name: 'Feedback',
      type: 'feedback',
      icon: 'thumbs-up-down-outline',
      textClass: 'text-md text-gray-700 font-medium py-1 prose',
      titleClass: 'text-sm text-gray-500',
      iconClass: 'rounded-full font-source font-semibold text-white',
    },
  };

  function getActorInfo(msg) {
    const actor = (msg[props.actorProp] || 'system').toLowerCase();
    return actorsInfo[actor] || actorsInfo['system'];
  }

  function getRole(msg) {
    const actor = (msg[props.actorProp] || 'system').toLowerCase();
    const actorInfo = actorsInfo[actor];
    if (actorInfo.type === 'agent') {
      return msg.name + ` ( ${actorInfo?.name} )`;
    }
    return actorInfo?.name || 'System';
    //return actor === 'user' ? 'User' : actor === 'bot' || actor === 'assistant' ? 'Bot' : 'System';
  }

  function getIcon(msg) {
    const actorInfo = getActorInfo(msg);
    if (actorInfo.type === 'feedback') {
      return msg?.feedbackScore < 4 ? 'thumb-down-outline' : 'thumb-up-outline';
    }
    return actorInfo?.icon || 'cog';
  }

  function mustShowMessage(transcriptItem) {
    if (messageIsToolRelated(transcriptItem)) {
      return false;
    }
    // Check for special kinds of system / internal messages todo improve that
    if ((transcriptItem[props.textProp] || '').indexOf('{"type"') !== -1) {
      return false;
    }
    if (transcriptItem?.kind === 'internal') {
      return false;
    }
    return true;
  }

  function messageHasSources(transcriptItem) {
    return Array.isArray(transcriptItem.sources) && transcriptItem.sources.length > 0;
  }

  function messageHasFeedback(transcriptItem) {
    return transcriptItem.id in conversationFeedbackMessages.value;
  }

  function feedbackForMessage(transcriptItem) {
    return conversationFeedbackMessages.value[transcriptItem.id] || [];
  }

  function messageIsToolRelated(transcriptItem) {
    return transcriptItem?.role === 'tool' || 'toolCalls' in transcriptItem || 'tool_call_id' in transcriptItem;
  }

  function actorIsBot(transcriptItem) {
    return getActorInfo(transcriptItem)?.type === 'bot';
  }

  function actorIsSystem(transcriptItem) {
    return getActorInfo(transcriptItem)?.type === 'system';
  }

  function actorIsCSat(transcriptItem) {
    return getActorInfo(transcriptItem)?.type === 'csat';
  }

  function actorIsFeedback(transcriptItem) {
    return getActorInfo(transcriptItem)?.type === 'feedback';
  }

  function getBotMessageHtml(transcriptItem) {
    return md.mdToHtml(transcriptItem[props.textProp] || '');
  }

  function getFeedbackHtml(transcriptItem) {
    return md.mdToHtml(transcriptItem[props.textProp] || '');
  }

  function getCSatMessageHtml(transcriptItem) {
    if (!transcriptItem.notruncate) {
      return (transcriptItem[props.textProp] || '').substring(0, 50) + ' ...';
    }
    return md.mdToHtml(transcriptItem[props.textProp] || '');
  }

  function getToolCallResultMessageHtml(transcriptItem) {
    if (!transcriptItem.notruncate) {
      return (transcriptItem[props.textProp] || '').substring(0, 100) + ' ...';
    }
    const mdTxt = '```\n' + (transcriptItem?.result || '') + '\n```\n';
    const mdHtml = md.mdToHtml(mdTxt);
    //return `<div>${transcriptItem[props.textProp]}<br><pre><code>${transcriptItem?.result || ''}</code></pre></div>`;
    return `<div>${transcriptItem[props.textProp]}<br>${mdHtml}`;
  }

  function getToolCallsMessageHtml(transcriptItem) {
    let text = transcriptItem[props.textProp];
    if (!text || text === '' || text === 'null') {
      text = 'Tool Calls';
    }
    if (!transcriptItem.notruncate) {
      return text.substring(0, 100) + ' ...';
    }
    return `<div>${text}<br><pre>${transcriptItem?.toolCalls || ''}</pre></div>`;
  }

  function getTranscriptTextClass(transcriptItem) {
    const detectedClass = getActorInfo(transcriptItem)?.textClass || 'text-md text-gray-700';
    // Process special cases
    if (actorIsBot(transcriptItem) && transcriptItem?.canceled) {
      return 'prose font-source text-sm rounded-md px-2 py-1 -ml-2 text-slate-400';
    }
    return detectedClass;
  }

  function getTranscriptTitleClass(transcriptItem) {
    return getActorInfo(transcriptItem)?.titleClass || 'text-sm';
  }

  function getTranscriptIconClass(transcriptItem) {
    const actorInfo = getActorInfo(transcriptItem);
    if (actorInfo.type === 'csat') {
      const bg = transcriptItem?.CSat < 4 ? 'bg-red-100' : transcriptItem?.CSat < 7 ? 'bg-yellow-100' : 'bg-green-100';
      return actorInfo?.iconClass + ' ' + bg;
    }
    if (actorInfo.type === 'feedback') {
      const bg = transcriptItem?.feedbackScore < 2 ? 'bg-red-800/70' : transcriptItem?.feedbackScore < 4 ? 'bg-orange-600/70' : 'bg-green-800/70';
      return actorInfo?.iconClass + ' ' + bg;
    }
    return actorInfo?.iconClass || 'rounded-full bg-slate-100 ring-8 ring-white';
  }

  function getFeedbackAnswerText(transcriptItem) {
    let txt = transcriptItem?.feedbackAnswer || '';
    if (txt.length > 100) {
      txt = txt.substring(0, 100) + '...';
    }
    return txt;
  }

  function startLoadingImages() {
    // Reset when the files array changes.

    const originalMessages = pathOr([], props.transcriptMessagesPath, props.conversation);
    for (let i = 0; i < originalMessages.length; i++) {
      const msg = originalMessages[i];
      if (actorIsSystem(msg) && msg?.kind === 'upload') {
        const filesList = msg?.files || null;
        if (Array.isArray(filesList) && filesList.length > 0) {
          msg.filesStates = reactive([]);
          for (const fileRecJson of filesList) {
            let fileRec = null;
            try {
              fileRec = JSON.parse(fileRecJson);
            } catch (e) {
              console.error('Failed to parse file record', e, fileRecJson);
              fileRec = null;
            }

            if (fileRec && 'name' in fileRec && 'path' in fileRec && 'type' in fileRec) {
              const state = reactive(
                Object.assign({}, fileRec, {
                  loading: true,
                  dataUrl: null,
                }),
              );
              msg.filesStates.push(state);

              api
                .storeDownloadFile({ path: state.path })
                .then((downloadResult) => {
                  if (downloadResult.success && downloadResult.data) {
                    state.dataUrl = `data:${state.type};base64,${downloadResult.data}`;
                  }
                })
                .catch((err) => {
                  state.dataUrl = null;
                  console.error(`Failed to load ${state.path}`, err);
                  // (optional) show a fallback image here
                })
                .finally(() => {
                  state.loading = false;
                });
            } else {
              console.error('Failed to process file record', fileRecJson);
            }
          }
        }
      }
    }
  }

  onMounted(() => {
    startLoadingImages();
  });
</script>
